/**
 * Week planning: several route days planned together.
 *
 * Each day is planned on a PROJECTED history: the real record, plus the planned swaps of the
 * days before it that haven't happened yet. So a pot planned for Monday isn't offered on
 * Tuesday (it'll be standing at Monday's client), and the pots Monday collects are back in
 * stock for later days. The projection lives only inside this function: plans never touch the
 * real record.
 *
 * A day that has already started (real events exist for it) is planned from the history as
 * it was that morning and is not projected: from then on, reality is what later days build on.
 */
import type { EventEnvelope } from "@hitl/core";
import type { CalendarDate } from "./calendar";
import type { Catalog } from "./catalog";
import { createAssessor, type Light } from "./assess";
import { buildHistory } from "./history";
import { checkPlan, suggestPlan, type DayPlan, type PlannerInput, type PlanProblem, type PlanReport } from "./planner";
import type { RepeatPolicy } from "./repeat-rule";

export interface WeekDay {
  readonly on: CalendarDate;
  /** Spots in driving order (from the route she placed on this day). */
  readonly slotIds: readonly string[];
}

export interface WeekInput {
  readonly catalog: Catalog;
  readonly realEvents: readonly EventEnvelope[];
  readonly policy: RepeatPolicy;
  readonly historyKnownSince: CalendarDate;
}

export interface WeekOptions {
  readonly spares?: (on: CalendarDate) => number;
  /** Her edits per day: slotId → potId. */
  readonly pinned?: (on: CalendarDate) => ReadonlyMap<string, string> | undefined;
}

export interface PlannedDay {
  readonly on: CalendarDate;
  readonly plan: DayPlan;
  readonly report: PlanReport;
  /** True when swaps have already been recorded for this day. */
  readonly started: boolean;
  /** The world as this day will find it: use for the spot sheet's candidates. */
  readonly input: PlannerInput;
}

export interface WeekReadout {
  readonly swaps: number;
  readonly fine: number;
  readonly check: number;
  readonly red: number;
  readonly open: number;
}

export interface WeekPlan {
  readonly days: readonly PlannedDay[];
  readonly readout: WeekReadout;
  readonly problems: readonly { readonly on: CalendarDate; readonly problem: PlanProblem }[];
}

const dayOf = (e: EventEnvelope): string | undefined => (e.payload as { on?: string } | null)?.on;

export function planWeek(days: readonly WeekDay[], input: WeekInput, options: WeekOptions = {}): WeekPlan {
  const { catalog, realEvents, policy, historyKnownSince } = input;
  const ordered = [...days].sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0));
  let projected: EventEnvelope[] = [...realEvents];
  let seq = 0;
  const planned: PlannedDay[] = [];

  for (const day of ordered) {
    const started = realEvents.some((e) => dayOf(e) === day.on);
    // A started day is judged against its morning; otherwise against everything projected so far.
    const basis = started ? projected.filter((e) => dayOf(e) !== day.on) : projected;
    const history = buildHistory(basis);
    const plannerInput: PlannerInput = {
      catalog,
      history,
      assessor: createAssessor({ catalog, history, policy, historyKnownSince }),
    };
    const pinned = options.pinned?.(day.on);
    const plan = suggestPlan({ on: day.on, slotIds: day.slotIds }, plannerInput, {
      spares: options.spares?.(day.on) ?? 2,
      ...(pinned ? { pinned } : {}),
    });
    const report = checkPlan(plan, plannerInput);
    planned.push({ on: day.on, plan, report, started, input: plannerInput });

    if (!started) {
      // Project this day's swaps so the following days see them.
      const simulated: EventEnvelope[] = [];
      const event = (type: string, payload: Record<string, unknown>): EventEnvelope => {
        seq += 1;
        return { id: `plan:${day.on}:${seq}`, type, schemaVersion: 1, occurredAt: `${day.on}T12:00:00+02:00`, recordedBy: "planner", deviceId: "~plan", deviceSeq: seq, payload };
      };
      for (const swap of plan.swaps) {
        if (!swap.potId) continue;
        const slot = catalog.slot(swap.slotId);
        const standing = history.occupantOf(swap.slotId)?.potId;
        if (standing && standing !== swap.potId) {
          simulated.push(event("pot.collected", { potId: standing, clientId: slot.clientId, slotId: slot.id, on: day.on }));
        }
        simulated.push(event("pot.placed", { potId: swap.potId, clientId: slot.clientId, slotId: slot.id, on: day.on }));
      }
      projected = [...projected, ...simulated];
    }
  }

  const count = (light: Light) =>
    planned.reduce((n, d) => n + d.report.swaps.filter((s) => s.potId && s.verdict?.light === light).length, 0);
  const swaps = planned.reduce((n, d) => n + d.plan.swaps.length, 0);
  const open = planned.reduce((n, d) => n + d.plan.swaps.filter((s) => s.potId === null).length, 0);

  return {
    days: planned,
    readout: { swaps, fine: count("green"), check: count("amber"), red: count("red"), open },
    problems: planned.flatMap((d) => d.report.problems.map((problem) => ({ on: d.on, problem }))),
  };
}
